import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SellPlan,
  SellSelection,
  SellUnitsCustomMode,
  SellUnitsMode,
} from 'modules/incentivesMarket/pages/IncentiveOfferDetail/types/SellUnitsSelection';

import {
  applyAmountDistribution,
  buildEmptySelection,
  computeSellTotals,
  computeTotalMaxUnits,
  distributeSellUnitsFifo,
} from '../utils/sellUnitsCalculations';

type UseSellUnitsSelectionArgs = {
  plans: SellPlan[];
  mustSellAll: boolean;
  minUnits: number;
  maxTotalUnits: number;
};

type UseSellUnitsSelectionResult = {
  data: {
    mode: SellUnitsMode;
    customMode: SellUnitsCustomMode;
    selected: Record<string, number>;
    customUnits: number;
    amountInput: string;
    totals: SellSelection['totals'];
    totalMax: number;
    totalMaxNet: number;
    pct: number;
    hasUnits: boolean;
    isConfirmEnabled: boolean;
    selection: SellSelection;
  };
  actions: {
    pickMode: (mode: SellUnitsMode) => void;
    setCustomMode: (mode: SellUnitsCustomMode) => void;
    setCustomTotalUnits: (value: number) => void;
    applyAmount: (raw: string) => void;
  };
};

export const useSellUnitsSelection = ({
  plans,
  mustSellAll,
  minUnits,
  maxTotalUnits,
}: UseSellUnitsSelectionArgs): UseSellUnitsSelectionResult => {
  const planTotalMax = useMemo(() => computeTotalMaxUnits(plans), [plans]);
  const totalMax = useMemo(
    () => Math.min(planTotalMax, maxTotalUnits),
    [planTotalMax, maxTotalUnits]
  );
  const totalMaxNet = useMemo(() => {
    const cappedSelection = distributeSellUnitsFifo(plans, totalMax);
    return computeSellTotals(plans, cappedSelection).net;
  }, [plans, totalMax]);

  const [selected, setSelected] = useState<Record<string, number>>(() =>
    buildEmptySelection(plans)
  );
  const [mode, setMode] = useState<SellUnitsMode>('none');
  const [customMode, setCustomModeState] =
    useState<SellUnitsCustomMode>('units');
  const [customUnits, setCustomUnits] = useState(0);
  const [amountInput, setAmountInput] = useState('');

  useEffect(() => {
    setSelected(buildEmptySelection(plans));
    setMode('none');
    setCustomModeState('units');
    setCustomUnits(0);
    setAmountInput('');
  }, [plans]);

  useEffect(() => {
    if (mustSellAll && plans.length > 0) {
      setSelected(distributeSellUnitsFifo(plans, totalMax));
      setMode('max');
      setCustomUnits(totalMax);
    }
  }, [mustSellAll, plans, totalMax]);

  const totals = useMemo(
    () => computeSellTotals(plans, selected),
    [plans, selected]
  );

  const pct = totalMax ? Math.round((totals.units / totalMax) * 100) : 0;

  const hasUnits = totals.units > 0;

  const isConfirmEnabled =
    hasUnits && totals.units >= minUnits && totals.units <= maxTotalUnits;

  const selection: SellSelection = useMemo(
    () => ({
      unitsByPlan: selected,
      totals,
    }),
    [selected, totals]
  );

  const pickMode = useCallback(
    (nextMode: SellUnitsMode) => {
      setMode(nextMode);
      if (nextMode === 'max') {
        setSelected(distributeSellUnitsFifo(plans, totalMax));
        setCustomUnits(totalMax);
      } else if (nextMode === 'half') {
        const halfUnits = Math.round(totalMax / 2);
        setSelected(distributeSellUnitsFifo(plans, halfUnits));
        setCustomUnits(halfUnits);
      } else if (nextMode === 'none') {
        setSelected(buildEmptySelection(plans));
        setCustomUnits(0);
      } else if (nextMode === 'custom') {
        setSelected(buildEmptySelection(plans));
        setCustomUnits(0);
        setAmountInput('');
      }
    },
    [plans, totalMax]
  );

  const setCustomMode = useCallback((nextMode: SellUnitsCustomMode) => {
    setCustomModeState(nextMode);
  }, []);

  const setCustomTotalUnits = useCallback(
    (value: number) => {
      const clamped = Math.max(
        0,
        Math.min(maxTotalUnits, Math.round(value || 0))
      );
      setCustomUnits(clamped);
      setMode('custom');
      setSelected(distributeSellUnitsFifo(plans, clamped));
    },
    [plans, maxTotalUnits]
  );

  const applyAmount = useCallback(
    (raw: string) => {
      setAmountInput(raw);
      setMode('custom');
      const next = applyAmountDistribution({
        plans,
        totalMaxNet,
        rawAmount: raw,
      });
      const nextTotal = Object.values(next).reduce(
        (sum, units) => sum + units,
        0
      );
      setCustomUnits(nextTotal);
      setSelected(next);
    },
    [plans, totalMaxNet]
  );

  return {
    data: {
      mode,
      customMode,
      selected,
      customUnits,
      amountInput,
      totals,
      totalMax,
      totalMaxNet,
      pct,
      hasUnits,
      isConfirmEnabled,
      selection,
    },
    actions: {
      pickMode,
      setCustomMode,
      setCustomTotalUnits,
      applyAmount,
    },
  };
};
